import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Layers, 
  Building2, 
  FileText, 
  DollarSign, 
  Calendar,
  ChevronRight,
  CheckCircle,
  Loader2,
  AlertCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsolidatedInvoicing = () => {
  const { getAuthHeaders } = useAuth();
  const [eligibleClients, setEligibleClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Generate invoice dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: getLastWeekStart(),
    end_date: getLastWeekEnd()
  });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Helper to get last week's dates
  function getLastWeekStart() {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const day = date.getDay();
    date.setDate(date.getDate() - day); // Go to Sunday
    return date.toISOString().split('T')[0];
  }

  function getLastWeekEnd() {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const day = date.getDay();
    date.setDate(date.getDate() - day + 6); // Go to Saturday
    return date.toISOString().split('T')[0];
  }

  const fetchEligibleClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/consolidation-eligible`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEligibleClients(data);
      }
    } catch (error) {
      toast.error('Failed to load eligible clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEligibleClients();
  }, []);

  const openGenerateDialog = (client) => {
    setSelectedClient(client);
    setPreview(null);
    setSelectedWorkOrders(client.unbilled_work_orders.map(wo => wo.work_order_id));
    setGenerateDialogOpen(true);
  };

  const fetchPreview = async () => {
    if (!selectedClient) return;
    
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      const response = await fetch(
        `${API_URL}/api/invoices/consolidation-preview/${selectedClient.client_id}?${params}`,
        {
          headers: getAuthHeaders(),
          credentials: 'include'
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
        setSelectedWorkOrders(data.work_orders.map(wo => wo.work_order_id));
      }
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleWorkOrder = (workOrderId) => {
    setSelectedWorkOrders(prev => 
      prev.includes(workOrderId)
        ? prev.filter(id => id !== workOrderId)
        : [...prev, workOrderId]
    );
  };

  const selectAll = () => {
    const allIds = (preview?.work_orders || selectedClient?.unbilled_work_orders || []).map(wo => wo.work_order_id);
    setSelectedWorkOrders(allIds);
  };

  const selectNone = () => {
    setSelectedWorkOrders([]);
  };

  const generateInvoice = async () => {
    if (selectedWorkOrders.length === 0) {
      toast.error('Please select at least one work order');
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/generate-consolidated`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          client_id: selectedClient.client_id,
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          include_work_order_ids: selectedWorkOrders
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setGenerateDialogOpen(false);
        fetchEligibleClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate invoice');
      }
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate selected total
  const selectedTotal = (preview?.work_orders || selectedClient?.unbilled_work_orders || [])
    .filter(wo => selectedWorkOrders.includes(wo.work_order_id))
    .reduce((sum, wo) => sum + (wo.billed_amount || 0), 0);

  // Total unbilled across all clients
  const totalUnbilled = eligibleClients.reduce((sum, c) => sum + c.unbilled_total, 0);

  return (
    <div className="space-y-6" data-testid="consolidated-invoicing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6 text-blue-600" />
            Consolidated Invoicing
          </h1>
          <p className="text-muted-foreground">
            Generate weekly invoices for enterprise clients
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Eligible Clients</p>
                <p className="text-2xl font-bold">{eligibleClients.length}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unbilled Work Orders</p>
                <p className="text-2xl font-bold">
                  {eligibleClients.reduce((sum, c) => sum + c.unbilled_count, 0)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={totalUnbilled > 0 ? 'ring-2 ring-green-400' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Unbilled</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalUnbilled)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients List */}
      <Card>
        <CardHeader>
          <CardTitle>Clients with Unbilled Work Orders</CardTitle>
          <CardDescription>Net 30 and consolidation-enabled clients</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : eligibleClients.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
              <p className="text-muted-foreground">No clients have unbilled work orders</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Unbilled Orders</TableHead>
                  <TableHead>Oldest Unbilled</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eligibleClients.map((client) => (
                  <TableRow key={client.client_id} data-testid={`client-row-${client.client_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{client.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {client.payment_terms?.replace('_', ' ') || 'Standard'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-orange-100 text-orange-800">
                        {client.unbilled_count} orders
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(client.oldest_unbilled)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-green-600">
                      {formatCurrency(client.unbilled_total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm"
                        onClick={() => openGenerateDialog(client)}
                        data-testid={`generate-invoice-${client.client_id}`}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Generate Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-600" />
              Generate Consolidated Invoice
            </DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Creating invoice for</p>
                <p className="font-semibold text-lg">{selectedClient.client_name}</p>
              </div>

              {/* Date Range */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Invoice Period
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={dateRange.start_date}
                      onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                      data-testid="consolidation-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={dateRange.end_date}
                      onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                      data-testid="consolidation-end-date"
                    />
                  </div>
                </div>
                <Button variant="outline" onClick={fetchPreview} disabled={loadingPreview}>
                  {loadingPreview ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Refresh Preview
                </Button>
              </div>

              {/* Work Orders Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Work Orders to Include
                  </h4>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                    <Button variant="outline" size="sm" onClick={selectNone}>Select None</Button>
                  </div>
                </div>

                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {(preview?.work_orders || selectedClient?.unbilled_work_orders || []).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                      <p>No work orders found for this period</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Work Order</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Languages</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(preview?.work_orders || selectedClient?.unbilled_work_orders || []).map((wo) => (
                          <TableRow key={wo.work_order_id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedWorkOrders.includes(wo.work_order_id)}
                                onCheckedChange={() => toggleWorkOrder(wo.work_order_id)}
                                data-testid={`select-wo-${wo.work_order_id}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{wo.work_order_id}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {wo.work_order_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {wo.languages?.join(' → ')}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(wo.billed_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Selected Work Orders</p>
                    <p className="font-semibold">{selectedWorkOrders.length} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Invoice Total</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedTotal)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateInvoice}
              disabled={generating || selectedWorkOrders.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-generate-invoice"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Generate Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsolidatedInvoicing;
